import { Search } from "lucide-react-native";
import React, { memo } from "react";
import { Platform } from "react-native";
import type { IMessage } from "react-native-gifted-chat";
import type { TextStyle, ViewStyle } from "react-native";

import MarkdownRenderer from "@/features/knowledge-graph/components/molecules/MarkdownRenderer";
import QueryProgressBar from "@/features/knowledge-graph/components/molecules/QueryProgressBar";
import SafeAndroidMarkdown from "@/features/knowledge-graph/components/molecules/SafeAndroidMarkdown";

import { Box } from "@/components/atoms/Box";
import { Icon } from "@/components/atoms/Icon";
import { Text } from "@/components/atoms/Text";

/**
 * Spacing and layout configuration for message bubble
 */
interface SpacingAndLayout {
  marginTop: number;
  marginBottom: number;
  marginLeft: number | "auto";
  marginRight: number | "auto";
  maxWidth: number;
  isAIMessage: boolean;
}

/**
 * Props for MessageBubbleView component
 */
interface MessageBubbleViewProps {
  message: IMessage;
  colors: {
    messageBubble: {
      background: string;
      userBackground: string;
      userText: string;
    };
  };
  isDark: boolean;
  isProgressMessage: boolean;
  spacingAndLayout: SpacingAndLayout;
  hasResearchMode: boolean;
  isAndroid: boolean;
  /** Role-specific testID: GEMINI_CHAT.AI_MESSAGE or GEMINI_CHAT.USER_MESSAGE */
  roleTestID: string;
  /** Role label for data-role attribute */
  roleLabel: "ai" | "user";
}

/*
 * design-system: dynamic-style waiver — computed per-message margins/maxWidth,
 * data-driven theme bubble/text colours, and RN text-flow properties that no
 * closed token expresses; passed as single style references so no inline
 * object literal renders on a JSX element.
 */
const outerStyle = (s: SpacingAndLayout): ViewStyle => ({
  marginTop: s.marginTop,
  marginBottom: s.marginBottom,
  marginLeft: s.marginLeft,
  marginRight: s.marginRight,
  maxWidth: s.maxWidth,
});
const bubbleBackgroundStyle = (
  isAIMessage: boolean,
  background: string,
  userBackground: string
): ViewStyle => ({
  backgroundColor: isAIMessage ? background : userBackground,
});
const colorStyle = (color: string): TextStyle => ({ color });
/*
 * QA-reported oversized query bubble: lineHeight must be an explicit
 * "22px" STRING on web. React DOM's inline-style engine treats a bare
 * NUMBER for CSS
 * `line-height` as a unitless multiplier of font-size (spec-compliant —
 * this is how CSS `line-height: 1.5` works), not pixels, while React
 * Native interprets a numeric `lineHeight` as absolute dp. `UNSAFE_style`
 * hands this object straight to the DOM `style` prop, bypassing
 * react-native-web's own StyleSheet compiler (which normally appends
 * "px"), so the same `22` that renders a normal 22dp line on iOS/Android
 * computed to `22 × 18px font-size = 396px` on web — ballooning the
 * single-line user query bubble into an oversized box (empty space above
 * and below the text) at every viewport width. Verified live on staging:
 * the rendered span's height dropped from 396px to 22px the instant the
 * unit was added.
 */
const userTextStyle = (
  color: string
): { color: string; lineHeight: number | string } => ({
  color,
  lineHeight: Platform.OS === "web" ? "22px" : 22,
});

/**
 * MessageBubbleView - Pure presentation component for message bubble
 * @param root0 - Component props
 * @param root0.message - Current message
 * @param root0.colors - Theme colors
 * @param root0.isDark - Whether dark theme is active
 * @param root0.isProgressMessage - Whether this is a progress message
 * @param root0.spacingAndLayout - Spacing and layout configuration
 * @param root0.hasResearchMode - Whether research mode is active
 * @param root0.isAndroid - Whether platform is Android
 * @param root0.roleTestID - Pre-resolved role-specific testID
 * @param root0.roleLabel - Role label for data-role attribute
 */
const MessageBubbleView = ({
  message,
  colors,
  isDark,
  isProgressMessage,
  spacingAndLayout,
  hasResearchMode,
  isAndroid,
  roleTestID,
  roleLabel,
}: MessageBubbleViewProps) =>
  isProgressMessage ? (
    <Box>
      <QueryProgressBar messageId={message._id} />
    </Box>
  ) : (
    <Box
      testID={roleTestID}
      {...{ "data-testid": roleTestID, "data-role": roleLabel }}
      // eslint-disable-next-line no-restricted-syntax -- genuine-runtime: computed per-message margins (marginLeft/Right may be 'auto') + data-driven maxWidth; no closed token
      UNSAFE_style={
        outerStyle(spacingAndLayout) as Record<string, string | number>
      }
    >
      <Box
        className="rounded-lg"
        // eslint-disable-next-line no-restricted-syntax -- genuine-runtime: data-driven theme bubble backgroundColor (colors.messageBubble.*); no fixed token set
        UNSAFE_style={
          bubbleBackgroundStyle(
            spacingAndLayout.isAIMessage,
            colors.messageBubble.background,
            colors.messageBubble.userBackground
          ) as Record<string, string | number>
        }
      >
        {spacingAndLayout.isAIMessage ? (
          <Box className="overflow-hidden rounded-md p-2">
            {isAndroid ? (
              <SafeAndroidMarkdown text={message.text} isDark={isDark} />
            ) : (
              <MarkdownRenderer content={message.text} />
            )}
          </Box>
        ) : (
          <Box className="p-3">
            {hasResearchMode && (
              <Box className="flex-row items-center gap-1 pb-2">
                <Icon
                  as={Search}
                  size="sm"
                  stroke={isDark ? "#60a5fa" : "#3b82f6"}
                />
                <Text
                  variant="unstyled"
                  className="font-medium text-caption"
                  // eslint-disable-next-line no-restricted-syntax -- genuine-runtime: theme-forked hardcoded blue hex with no verified semantic-token mapping (pixel-verification required)
                  UNSAFE_style={
                    colorStyle(isDark ? "#60a5fa" : "#3b82f6") as Record<
                      string,
                      string | number
                    >
                  }
                >
                  Research Mode
                </Text>
              </Box>
            )}
            <Text
              variant="unstyled"
              className="max-w-full shrink flex-wrap text-title"
              // eslint-disable-next-line no-restricted-syntax -- genuine-runtime: data-driven theme text color (colors.messageBubble.userText) + off-ladder lineHeight 22
              UNSAFE_style={
                userTextStyle(colors.messageBubble.userText) as Record<
                  string,
                  string | number
                >
              }
            >
              {message.text}
            </Text>
          </Box>
        )}
      </Box>
    </Box>
  );

MessageBubbleView.displayName = "MessageBubbleView";

export default memo(MessageBubbleView);
